import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import EmptyState from "../../components/EmptyState";
import api from "../../lib/api";
import {
  Briefcase,
  TrendingUp,
  Users,
  MousePointerClick,
  Eye,
  Award,
  BarChart3,
  Download,
  Loader2,
  Building2,
  Sparkles,
} from "lucide-react";

export default function SponsorDashboard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sponsors/me")
      .then((r) => api.get(`/sponsors/${r.data.id}/report`))
      .then((r) => setReport(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto py-20 text-center">
        <Loader2 className="h-6 w-6 text-[#0F4C81] animate-spin mx-auto" />
      </div>
    </DashboardLayout>
  );

  if (!report) return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto py-14">
        <EmptyState
          icon={Briefcase}
          eyebrow="Sponsor Dashboard"
          title="Your sponsor profile isn't linked yet"
          description="Sponsor dashboards show impressions, click-throughs, leads collected and ROI per conference. Ask your event organiser to link your sponsor profile to your Diplomexa account."
          primary={{ label: "Contact your event team", onClick: () => window.location.href = "mailto:info@factskoot.com?subject=Link%20sponsor%20profile" }}
          secondary={{ label: "Back to overview", onClick: () => window.history.back() }}
          testId="sponsor-empty"
        />
      </div>
    </DashboardLayout>
  );

  const s = report.sponsor;
  const m = report.metrics;

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto pb-16">
        {/* Hero header */}
        <div className="rounded-2xl bg-[#0A192F] text-white p-8 lg:p-10 border border-[#D4AF37]/30 relative overflow-hidden" data-testid="sponsor-hero">
          <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#D4AF37]/10 blur-3xl" />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
                Sponsor Portal
              </p>
              <h1 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tighter">
                {s.name}
              </h1>
              <p className="mt-2 text-slate-300 text-sm font-serif italic">{s.description}</p>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <span className="px-3 py-1 rounded-full bg-[#D4AF37] text-[#0A192F] text-xs font-bold uppercase tracking-[0.2em]" data-testid="sponsor-package">
                  {s.package} package
                </span>
                <span className="text-xs text-slate-400">{s.event_ids?.length || 0} event(s) sponsored</span>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-xl bg-white/5 border border-white/10 p-5">
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
                  ROI snapshot
                </p>
                <div className="mt-3 flex items-end gap-3">
                  <p className="font-serif text-5xl text-[#D4AF37]">{m.roi_score}</p>
                  <p className="text-sm text-slate-300 mb-1">/ 100</p>
                </div>
                <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#D4AF37] to-[#f6d272]"
                    style={{ width: `${m.roi_score}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Metric cards */}
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="sponsor-metrics">
          <Metric icon={Eye} label="Impressions" value={m.impressions.toLocaleString()} accent="text-[#0F4C81]" />
          <Metric icon={MousePointerClick} label="Clicks" value={m.clicks.toLocaleString()} accent="text-[#0F4C81]" />
          <Metric icon={Users} label="Leads generated" value={m.leads} accent="text-emerald-600" />
          <Metric icon={TrendingUp} label="Brand visibility" value={`${m.brand_visibility_score}/100`} accent="text-[#D4AF37]" />
          <Metric icon={Building2} label="Delegate reach" value={m.delegate_reach} accent="text-[#0F4C81]" />
          <Metric icon={Award} label="Sponsored awards" value={m.sponsored_awards} accent="text-[#D4AF37]" />
          <Metric icon={Sparkles} label="Sponsored sessions" value={m.sponsored_sessions} accent="text-[#0F4C81]" />
          <Metric icon={Award} label="Package tier" value={s.package} accent="text-[#0A192F]" capitalize />
        </div>

        {/* Two-column: Brand + Leads */}
        <div className="mt-8 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-5 rounded-2xl bg-white border border-slate-200 p-6">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Brand assets</p>
            <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">Logo & placement</h2>
            <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-6 flex items-center justify-center h-32">
              {s.logo_url ? (
                <img src={s.logo_url} alt={s.name} className="max-h-20 max-w-full object-contain" />
              ) : (
                <p className="text-xs text-slate-400">No logo uploaded yet</p>
              )}
            </div>
            <div className="mt-4 space-y-2">
              <Row label="Banner placement" value={s.banner_placement || "—"} />
              <Row label="Website" value={s.website || "—"} link={s.website} />
              <Row label="Contact" value={s.contact_email || "—"} />
            </div>
            {s.sponsored_awards?.length > 0 && (
              <div className="mt-5">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Sponsored awards</p>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {s.sponsored_awards.map((a) => (
                    <span key={a} className="px-2.5 py-1 rounded-full bg-[#D4AF37]/15 text-[#0A192F] text-xs font-bold">{a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="lg:col-span-7 rounded-2xl bg-white border border-slate-200 p-6" data-testid="sponsor-leads">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Leads</p>
                <h2 className="mt-1 text-xl font-semibold tracking-tight text-[#0A192F]">Delegate enquiries</h2>
              </div>
              <button className="text-xs uppercase tracking-[0.2em] font-bold text-[#0F4C81] inline-flex items-center gap-1.5">
                <Download className="h-3.5 w-3.5" /> Export CSV
              </button>
            </div>
            <div className="mt-5 max-h-[420px] overflow-y-auto">
              {report.leads.length === 0 ? (
                <p className="py-10 text-center text-sm text-slate-500">No leads yet — kick off lead capture on event pages.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 border-b">
                    <tr>
                      <th className="py-2.5 font-semibold">Delegate</th>
                      <th className="py-2.5 font-semibold">Email</th>
                      <th className="py-2.5 font-semibold">Interest</th>
                      <th className="py-2.5 font-semibold">When</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.leads.map((l) => (
                      <tr key={l.id} className="hover:bg-slate-50/80">
                        <td className="py-3 font-semibold text-[#0A192F]">{l.delegate_name}</td>
                        <td className="py-3 text-slate-600 font-mono text-xs">{l.delegate_email}</td>
                        <td className="py-3 text-slate-700 text-xs">{l.interest || "—"}</td>
                        <td className="py-3 text-slate-500 text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Visibility tagline */}
        <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-white p-7 flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">Sponsor positioning</p>
            <p className="mt-2 text-xl font-serif italic">"Give sponsors measurable visibility, not just logo placement."</p>
          </div>
          <BarChart3 className="h-12 w-12 text-[#D4AF37] opacity-60" />
        </div>
      </div>
    </DashboardLayout>
  );
}

function Metric({ icon: Icon, label, value, accent, capitalize }) {
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <Icon className={`h-4 w-4 ${accent}`} />
      <p className={`mt-3 font-serif text-3xl ${accent} ${capitalize ? "capitalize" : ""}`}>{value}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</p>
    </div>
  );
}

function Row({ label, value, link }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500 text-xs">{label}</span>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" className="text-[#0F4C81] hover:underline font-semibold truncate max-w-[60%]">{value}</a>
      ) : (
        <span className="text-[#0A192F] font-semibold truncate max-w-[60%]">{value}</span>
      )}
    </div>
  );
}
